import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useFetchTransactionBreakdownByType } from "@transactionHooks";
import { usePortfolioSummary } from "@/hooks/usePortfolioSummary";
import { ReportProps } from "../common.props";
import IncomeCategoryRow from "./IncomeCategoryRow";
import { formatNumber } from "@/lib/currency.utils";
import { useTransactions } from "@/hooks/useTransactions";
import { useMemo } from "react";

export function IncomePanel({ displayCurrency, convert }: ReportProps) {
  const { data: categories, isLoading: catLoading } =
    useFetchTransactionBreakdownByType("income");
  const { data: portfolio, isLoading: portLoading } = usePortfolioSummary();

  const twelveMonthsAgoDate = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 11);
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  }, []);

  const { data: incomeTx = [], isLoading: txLoading } = useTransactions({
    type: 'income',
    dateFrom: twelveMonthsAgoDate,
  });

  const isLoading = catLoading || portLoading || txLoading;

  const trendData = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date();
      d.setMonth(d.getMonth() - (11 - i));
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      
      let totalIncome = 0;
      if (incomeTx) {
        incomeTx.forEach(tx => {
          if (tx.transaction_date.startsWith(monthKey)) {
            totalIncome += convert(tx.amount, tx.currency, displayCurrency);
          }
        });
      }

      return {
        month: d.toLocaleString("default", { month: "short", year: "2-digit" }),
        income: totalIncome,
      };
    });
  }, [incomeTx, convert, displayCurrency]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Income</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <p className="text-sm text-muted-foreground">Loading...</p>
        )}

        {!isLoading && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Total Income</p>
                <p className="text-2xl font-semibold">
                  {formatNumber(portfolio?.totalIncome ?? 0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Net Income</p>
                <p className="text-2xl font-semibold">
                  {formatNumber(portfolio?.netSavings ?? 0)}
                </p>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(categories ?? []).map((cat) => (
                  <IncomeCategoryRow
                    key={cat.categoryId}
                    cat={cat}
                    displayCurrency={displayCurrency}
                    convert={convert}
                  />
                ))}
              </TableBody>
            </Table>

            <div aria-label="12-month income trend" className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData}>
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => formatNumber(value, 2)} />
                  <Tooltip formatter={(value: number) => formatNumber(value, 2)} />
                  <Bar dataKey="income" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
